import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { MailCheck } from "lucide-react";
import { Button, TextField } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import type { MeDTO, MfaChallengeDTO } from "@shared/types";
import { MfaStep } from "@/components/MfaStep";
import { AuthLayout } from "./AuthLayout";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/forgot", { email });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout title="Check your email" back="/login">
        <div className="flex flex-col items-center gap-3 text-center">
          <MailCheck className="size-12 text-brand" />
          <p>
            If <b>{email}</b> has a parent account, we've sent a link to reset the password. It works once and expires in an hour.
          </p>
          <p className="text-sm text-muted">Nothing there? Check your spam folder, or try again in a few minutes.</p>
          <Link to="/login" className="mt-2 font-bold text-brand">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Forgot your password?" subtitle="Enter your email and we'll send you a link to choose a new one." back="/login">
      <form onSubmit={submit} className="space-y-4">
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
        />
        <Button type="submit" size="lg" block loading={busy}>
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<MeDTO | MfaChallengeDTO>("/auth/reset", { token, password });
      if ("mfaRequired" in res) setTicket(res.ticket);
      else {
        qc.setQueryData(["me"], res);
        navigate("/parent", { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset your password");
    } finally {
      setBusy(false);
    }
  }

  if (ticket) {
    return (
      <AuthLayout title="Password changed" subtitle="Two-step verification is on, so enter a code to finish signing in." back="/login">
        <MfaStep
          ticket={ticket}
          onDone={(me) => {
            qc.setQueryData(["me"], me);
            navigate("/parent", { replace: true });
          }}
          onRestart={() => navigate("/login", { replace: true })}
        />
      </AuthLayout>
    );
  }

  if (!token) {
    return (
      <AuthLayout title="Link not valid" back="/login">
        <p className="text-center">
          This reset link is incomplete.{" "}
          <Link to="/forgot-password" className="font-bold text-brand">
            Request a new one
          </Link>
          .
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="You'll be signed out on your other devices." back="/login">
      <form onSubmit={submit} className="space-y-4">
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          required
          hint="At least 8 characters"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error}
        />
        <Button type="submit" size="lg" block loading={busy}>
          Save and sign in
        </Button>
        {error && (
          <p className="text-center text-sm">
            <Link to="/forgot-password" className="font-bold text-brand">
              Send a new link
            </Link>
          </p>
        )}
      </form>
    </AuthLayout>
  );
}
